// Deterministic-session guard for the flight booking flow. A "search nonce"
// tags one booking attempt (Search -> Review -> Details -> Seats ->
// Itinerary); every priced context (`bag`) carries the nonce that was active
// when it was created. Before any async response is allowed to write back
// into redux, callers compare the nonce it was fetched under against
// whatever is currently active — a stale/late response (from an abandoned
// search or a race with a newer one) is discarded instead of silently
// overwriting the live TUI, which is what caused checklist and seat-layout
// to end up holding two different priced_tui values for Benzy.

import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { addonChargesFromBag } from "./flightItinerary";

export const FLIGHT_BAG_STORAGE_KEY = 'flightReactBookingBag'

export const persistFlightBookingBag = (bag) => {
  if (!bag || typeof bag !== 'object') return
  try {
    sessionStorage.setItem(FLIGHT_BAG_STORAGE_KEY, JSON.stringify(bag))
  } catch {
    // ignore quota / private mode
  }
}

export const loadFlightBookingBag = () => {
  try {
    const raw = sessionStorage.getItem(FLIGHT_BAG_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

export const clearFlightBookingBagStorage = () => {
  try {
    sessionStorage.removeItem(FLIGHT_BAG_STORAGE_KEY)
  } catch {
    // ignore
  }
}

export const generateSearchNonce = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

// True when a response fetched under `responseNonce` is no longer relevant
// because the app has since moved on to a different search/booking attempt.
// `activeNonce` missing (not yet set) never counts as stale — that's the
// pre-first-search boot state, not an abandoned attempt.
export const isNonceStale = (responseNonce, activeNonce) =>
  Boolean(activeNonce) && Boolean(responseNonce) && responseNonce !== activeNonce;

export const logTui = (label, tui) => {
  if (process.env.NODE_ENV === "production") return;
  // eslint-disable-next-line no-console
  console.debug(`[flight-tui] ${label}:`, tui);
};

/**
 * Fare-group prices EVERY fare type; Benzy's "current" session TUI often
 * becomes the *last* priced one. Checklist/seat responses can then echo a
 * different second-segment TUI than the fare the user actually selected.
 * Preview UI never adopts that rotation — keep the fare-selected TUI pinned.
 */
export const pinnedPricedTui = (bag) =>
  String(bag?.source_priced_tui || bag?.priced_tui || "").trim();

/**
 * Merge a Benzy echo payload into the bag without rotating priced_tui away
 * from the fare-selected / request TUI.
 */
export const mergeBagKeepingPricedTui = (bag, echo, requestTui) => {
  const pinned = String(requestTui || pinnedPricedTui(bag) || "").trim();
  const incoming = echo && typeof echo === "object" ? { ...echo } : {};
  // Never let supplier TUI / priced_tui overwrite the pinned selection.
  delete incoming.TUI;
  delete incoming.priced_tui;
  return {
    ...bag,
    ...incoming,
    priced_tui: pinned,
    source_priced_tui: bag?.source_priced_tui || pinned,
  };
};

// A ref mirror of flightBooking.activeSearchNonce, always current inside an
// async .then()/.catch() closure — a plain destructured selector value would
// be frozen at the render the request fired from, missing any reset/new
// search that happened while the request was in flight.
export const useActiveSearchNonceRef = () => {
  const activeSearchNonce = useSelector((s) => s.flightBooking.activeSearchNonce);
  const ref = useRef(activeSearchNonce);
  useEffect(() => {
    ref.current = activeSearchNonce;
  }, [activeSearchNonce]);
  return ref;
};

/**
 * Merge API echo into bag (pinned TUI) — preserve review add-ons when checklist echoes 0 SSR.
 */
export const mergeFlightApiEcho = (bag, echo, requestTui) => {
  const merged = mergeBagKeepingPricedTui(bag, echo, requestTui)
  const localAddons = addonChargesFromBag(bag)
  const incoming = echo && typeof echo === 'object' ? echo : {}
  const incomingSsr = Number(incoming.SSRAmount)
  const keepLocalAddons = localAddons > 0
    && (incoming.SSRAmount == null || incoming.SSRAmount === '' || incomingSsr === 0)

  const out = {
    ...merged,
    ...(incoming.seat_layout_payload && typeof incoming.seat_layout_payload === 'object'
      ? { seat_layout_payload: incoming.seat_layout_payload }
      : {}),
    ...(incoming.sabre_seat_layout && typeof incoming.sabre_seat_layout === 'object'
      ? { sabre_seat_layout: incoming.sabre_seat_layout }
      : {}),
    ...(incoming.checkout_ssr_mode ? { checkout_ssr_mode: incoming.checkout_ssr_mode } : {}),
  }

  if (keepLocalAddons) {
    out.SSRAmount = String(bag.SSRAmount ?? localAddons)
    out.SSR = bag.SSR ?? out.SSR
    out.ssr_addons = bag.ssr_addons ?? out.ssr_addons
    if (bag.total != null) out.total = bag.total
    if (bag.total_label) out.total_label = bag.total_label
  }

  return out
}

const SEAT_MAP_CLIENT_KEYS = ['Trips', 'seat_layout_payload', 'sabre_seat_layout']

/**
 * Travel-checklist echo — same as Akbar: fare/checklist fields only, never seat map bodies.
 */
export const mergeTravelChecklistEcho = (bag, echo, requestTui) => {
  const merged = mergeFlightApiEcho(bag, echo, requestTui)
  SEAT_MAP_CLIENT_KEYS.forEach((key) => {
    delete merged[key]
  })
  return merged
}

// Shared Benzy/HTTP session-expiry detection used by travel-checklist,
// seat-layout and itinerary — Code 501/1601/1025 ("No Data Found" /
// "session_expired") and a plain HTTP 410 all mean the priced TUI this
// screen was holding is no longer valid on Benzy's side.
const SESSION_EXPIRED_BENZY_CODES = new Set(["501", "1601", "1025"]);

export const isSessionExpiredError = (err) => {
  const body = err.response?.data?.data || err.response?.data || {};
  const code = body.benzy_code || body.code;
  const codeStr = String(code || "");
  return (
    err.response?.status === 410 ||
    body.code === "session_expired" ||
    SESSION_EXPIRED_BENZY_CODES.has(codeStr)
  );
};
